/**
 * This module contains UI
 * widgets to represent instructions
 */

(function () {
  require("./instruction_widgets.css");

  const $ = require('jQuery');
  const ml = require('../machinelogic');
  const widgets = require('./widgets');
  const icons = require('../icons');
  const tooltips = require('./machinelogic_tooltips');
  const helpers = require('../helpers');
  const utils = require('./gui_instructions/utils');
  const esprima = require('esprima');
  const getLeftOperatorRightFromExpression = require('../validate_expression').getLeftOperatorRightFromExpression;

  const { autocompleteComponent } = require('./vse_ui/vse_components/base_components');

  // Constants
  const LEFT_OPERAND = 0;
  const OPERATOR = 1;
  const RIGHT_OPERAND = 2;
  const PARAMETER_LABEL_SUFIX = ' / Variable';
  const OPERATOR_TO_TEXT = Object.freeze({
    '==': 'Equal to',
    '!=': 'Not equal to',
    '<=': 'Less than or equal to',
    '>=': 'Greater than or equal to',
    '<': 'Less than',
    '>': 'Greater than',
  });
  const operatorOptionsList = Object.freeze(Object.keys(OPERATOR_TO_TEXT).map((operatorSymbol) => {
    return {
      value: operatorSymbol,
      text: OPERATOR_TO_TEXT[operatorSymbol]
    };
  }));

  /**
   * Drop-down list for the axis
   * @param {ml.ConfigManager} pConfigManager - Machine Logic Config Manager object
   * @param {ml.Instruction} pInstruction - Machine Logic Instruction object
   * @param {String} pFullName - Axis full name, when combine moves [axes.0.axis]
   * @param {String} pName - Label for the widget
   * @param {String} pTooltip - Message showed on hovering
   * @param {Boolean} pEmptyAllowed - Allow have a empty option on the drop-down
   * @param {String[]} pAllowedAxisType - An array with the list of actuators allowed for the instruction
   * @param {String} pInitialParameter - Initial 1-based persisted axis parameter
   * @param {Function} pOnValue - Function called on change
   */
  function axisParamWidget(pConfigManager, pInstruction, pFullName, pName, pTooltip, pEmptyAllowed, pAllowedAxisType, pInitialParameter, pOnValue) {
    if (pName === 'axis') {
      pName = 'actuator';
    }

    function lAxisOption(pAxis, pDisabled) {
      const lOption = $('<option value="' + pAxis.getUUID() + '">' + (pAxis != undefined ? pAxis.getName() : 'None') + '</option>');
      // Note: disabled only options that already in use
      // in case of combine movements VentionCo/mm-vention-control#862
      if (pDisabled) {
        lOption.attr('disabled', 'disabled');
      }

      return lOption;
    }

    const _lWidget = widgets.baseWidget(pName);
    const _lAxisWidget = widgets.select(pTooltip);
    const lAxisSelect = _lAxisWidget.getSelect();
    const _setAxisValue = function (pValue) {
      pConfigManager.notify('onResetAxisError'); // Note (Aj): This notification is used by assembler, do NOT remove!
      pOnValue(pValue == null || pValue == '' ? ml.reservedUuids.NONE : pValue);
      pInstruction.notify('onAxisChanged', pValue);
    };

    // Populate axis dropdown
    const lAxisParametersNames = pInstruction.listAxisParametersNames();
    const lIsAxisTypeIncompatible = function (pAxisUuid) {
      let _lIncompatible = false;
      // Check if there is a restriction on axisType
      if (pAllowedAxisType) {
        if (!pAllowedAxisType.includes(pConfigManager.getAxisByUuid(pAxisUuid).getType())) {
          _lIncompatible = true;
        }
      }
      return _lIncompatible;
    };
    const lIsAxisUsedInOtherParams = function (pAxisUuid) {
      // Applicable to multi-axes (aka combined) instructions.
      // List all other axis parameters to be able to disable options that are already used.
      return !!lAxisParametersNames
        .filter(function(pParamInfoName) { return pParamInfoName != pFullName; })
        .find(function(pParamInfoName) { return pInstruction.getParameter(pParamInfoName) == pAxisUuid; });
    };
    const lDoesAxisBelongToDifferentController = function(pAxis) {
      // Applicable to multi-axes (aka combined) instructions.
      let lFirstAxis;
      const lFirstSelectedAxisName = lAxisParametersNames.find(function (lDependantParamInfoName) {
        const lAxisUuid = pInstruction.getParameter(lDependantParamInfoName);
        const lAxis = pConfigManager.getAxisByUuid(lAxisUuid);
        if (lAxis) { lFirstAxis = lAxis; }
        return lAxis !== undefined;
      });
      if (!lFirstSelectedAxisName) { return false; } // This technically is impossible since we ensure that atleast one axis is selected in a combined move
      return lFirstAxis.getControllerId() !== pAxis.getControllerId();
    };

    // Enable/disable axis dropdown values
    const lIsCombinedMove = /combined/i.test(pInstruction.getName());
    const lIsSetVariable = /fromActuatorPosition/i.test(pInstruction.getName());

    pConfigManager.getConfigurationList().forEach(function(pConfig) {
      const lController = pConfig.getController();
      const lOptGroup = $('<optgroup>').attr('label', lController.getName()).appendTo(lAxisSelect);

      pConfig.listAxes().forEach(function (pAxis) {
        // For combined moves, disable option if the axis is already used or its type is incompatible
        let lDisabled = false;
        // NOTE: for single moves we want to allow all actuators
        if (lIsCombinedMove) {
          if(lIsAxisTypeIncompatible(pAxis.getUUID()))                          { lDisabled = true; }
          else if(lIsCombinedMove && lIsAxisUsedInOtherParams(pAxis.getUUID())) { lDisabled = true; }
          else if(lDoesAxisBelongToDifferentController(pAxis))                  { lDisabled = true; }
        }
        // For setVariable, disable option if its type is incompatible
        else if (lIsSetVariable) {
          if(lIsAxisTypeIncompatible(pAxis.getUUID()))                          { lDisabled = true; }
        }
        lOptGroup.append(lAxisOption(pAxis, lDisabled));
      });
    });

    if(pEmptyAllowed) { lAxisSelect.append($('<option>', { value: ml.reservedUuids.NONE, text: '(Not selected)', })); }

    lAxisSelect.on('change', function (pEvent) {
      pConfigManager.notify('onResetAxisError'); // Note (Aj): This notification is used by assembler, do NOT remove!
      pEvent.stopPropagation();
      // Note (Aj): If the instruction is a combined move and the user tries to set all the axis to NONE (Not Selected) show warning and revert changes
      if (lAxisSelect.val() == ml.reservedUuids.NONE && pInstruction.getName().match(/Combined/i)) {
        if(Object.values(pInstruction.getParameter('axes')).filter(axis=> axis.axis != undefined && axis.axis != ml.reservedUuids.NONE).length == 1) {
          lAxisSelect.val(pInstruction.getParameter('axes')[pFullName.split('.')[1]].axis);
          widgets.messageBox('Combined move command must include at least one axis', 'warning');
          return;
        }
      }

      // remove the color and (Not Configured) as this card is not rebuilt, note: this must be done after validating that there is at least 1 drive
      if (pInstruction.getName().match(/Combined/i)) {
        lAxisSelect.removeClass('ml_validator_red');
        lAxisSelect.find(`option[value="${ml.reservedUuids.UNKNOWN}"]`).remove();
      }

      _setAxisValue(lAxisSelect.val());
    });
    // Note (alevy): Added an "all actuators" option so it filters out some specific instructions
    // don't add the "all actuators" option if it is the moveCombined instruction. See ticket: mm-vention-control#862
    // and don't add the all actuators option if it is a setVariable sequence
    if( !lIsCombinedMove && pInstruction.getCategory() !== "setVariable") {
      lAxisSelect.prepend($('<option>', { value: ml.reservedUuids.ALL, text: 'All Actuators' }));
    }


    // Initialize select
    switch (pInitialParameter) {
      case ml.reservedUuids.ANY: {
        // Note (mkosarek) Find the first axis that is possible here
        const _lDefaultAxis = pConfigManager.listAxes().find(function(pAxis) {
          return !lIsAxisUsedInOtherParams(pAxis.mUUID) && !lIsAxisTypeIncompatible(pAxis.mUUID);
        });

        lAxisSelect.val(_lDefaultAxis ? _lDefaultAxis.getUUID() : ml.reservedUuids.NONE);
        _setAxisValue(lAxisSelect.val());
        break;
      }
      case ml.reservedUuids.ALL: {
        lAxisSelect.val(ml.reservedUuids.ALL);
        break;
      }
      case ml.reservedUuids.UNKNOWN: {
        lAxisSelect.append($('<option>', { value: ml.reservedUuids.UNKNOWN, text: '(Not Configured)', disabled: true }));
        lAxisSelect.val(ml.reservedUuids.UNKNOWN);
        lAxisSelect.addClass('ml_validator_red');

        // This is to remove the RED on invalid option when the drop down is openeded
        // NOTE: we will reaply the error-color on the next event, this is not optimal.
        // FIXME: we want to capture the case when the user click outside the drop down menu without making a choice,
        // unfortunatelly, no event are generated until the next click. I have tried on(blur, mousedown),
        // tried to attach a click/mouseup to the document,
        // the browser seems to fire them only after the drop down is closed, so we never know the exact moment when the
        // dropdown is closed. To go around would proably require a custom select widget.
        lAxisSelect.on('click', function() { lAxisSelect.removeClass('ml_validator_red'); });
        // NOTE: so apparently, lAxisSelect.val() will be null if the option is disabled... that is unfortunate....
        lAxisSelect.on('focusout', function() { if(lAxisSelect[0].value == ml.reservedUuids.UNKNOWN) { lAxisSelect.addClass('ml_validator_red'); } });
        lAxisSelect.on('keydown', function(pEvent) { if(pEvent.keyCode == 40) { lAxisSelect.removeClass('ml_validator_red'); } });
        break;
      }
      case ml.reservedUuids.NONE:
      case undefined: {
        lAxisSelect.val(ml.reservedUuids.NONE);
      }
        break;
      default: {
        lAxisSelect.val(pInitialParameter);
        break;
      }
    }

    _lWidget.append(_lAxisWidget);

    return _lWidget;
  }

  /**
   * Bare bones card widget
   * @returns {JQuery} JQuery element
   */
  function box() {
    const lDiv = $('<div class="ml_card" />');
    return lDiv;
  }

  /**
   * Create condition widget
   * @param {Object} pParams
   * @param {ml.Library} pParams.library - Machine Logic Library Object
   * @param {ml.Application} pParams.application - Machine Logic Application Object
   * @param {ml.Sequence} pParams.sequence - Machine Logic Sequence Object
   * @param {ml.instruction} pParams.instruction - Machine Logic Instruction Object
   * @return {JQuery} Jquery element
   */
  function condition(pParams) {
    const lContainer = conditionalLayout({ instruction: pParams.instruction, sequence: pParams.sequence, icon: icons.conditionNew });
    const lInstructionObject = pParams.instruction;
    const lApplicationObject = pParams.application;
    const lConditionManager = new ml.ConditionManager();
    // make do/undo easier
    const lConditionsUIMap = {};
    // TODO: wrap this in a function
    const addStatement = function (condition, loadFromJSON) {
      const lConditionParams = {
        instruction: lInstructionObject,
        sequence: pParams.sequence,
        parameter: condition,
        application: lApplicationObject,
        conditionManager: lConditionManager,
      };

      const lIndex = lConditionManager.addCondition(condition, lInstructionObject, loadFromJSON);
      lConditionParams.id = lIndex;

      if (condition.instructionType != 'if') {
        lConditionParams.onClose = function () {
          lLogicalExpression.remove();
          lConditionManager.removeCondition(lIndex, lInstructionObject);
          delete lConditionsUIMap[lIndex];
        };
      }
      const lLogicalExpression = logicalExpression(lConditionParams);
      lConditionsUIMap[lIndex] = lLogicalExpression;
      lContainer.body.append(lLogicalExpression);
    };
    const lFooterContainer = conditionFooter({ instruction: lInstructionObject, addStatement: addStatement, conditionManager: lConditionManager });
    const filterStatements = function (keyword) { return function (obj) { return obj.instructionType === keyword; }; };
    const lConditions = lInstructionObject.getParameter('conditions');
    const lIfStatement = lConditions.filter(filterStatements('if'));
    const lElseIfstatement = lConditions.filter(filterStatements('elseIf'));
    const lElseStatement = lConditions.filter(filterStatements('else'));

    const lConditionManagerListener = {
      onConditionRemoved: function(pIndex) {
        if (!lConditionsUIMap.hasOwnProperty(pIndex)) { return; }
        lConditionsUIMap[pIndex].remove();
      },
      onConditionAdded: function( pCondition ) { console.log('pCondition', pCondition);
        addStatement(pCondition); },
    };
    lConditionManager.addListener(lConditionManagerListener);
    // Populate the statements
    // add the if conditional first
    lIfStatement.forEach(function(condition) { addStatement(condition, true); });
    // else ifs
    lElseIfstatement.forEach(function (condition) { addStatement(condition, true); });
    // else
    lElseStatement.forEach(function (condition) { addStatement(condition, true); });

    lContainer.on('remove', function onRemoveCondition() {
      lConditionManager.removeListener(lConditionManagerListener);
    });

    lContainer.append(lFooterContainer);

    return lContainer;
  }

  /**
   * Build conditional expression
   * @param {Object} pParams
   * @param {ml.Application} pParams.application
   * @param {JQuery} pParams.container
   * @param {String} pParams.expression
   * @param {ml.Instruction} pParams.instruction
   * @param {Number} pParams.id - optional
   * @param {{ instructionType: String, conditionExpression: String, execute: String, sequenceName: String}} pParams.instructionParam
   * @param {Function} pParams.onValue
   * @returns
   */
  function conditionExpression(pParams) {
    const isElseType = pParams.hasOwnProperty('instructionParam') && pParams.instructionParam['instructionType'] === 'else';

    const lConditionExpressionValues = isElseType ? [] : getLeftOperatorRightFromExpression(pParams.expression);
    let lInstructionListener = {};

    const lLeftOperand = autocompleteComponent(
      'Value / Variable',
      'Operand value, a variable or a number',
      lConditionExpressionValues[LEFT_OPERAND],
      pParams.application.listAllApplicationParameterNames(),
      (pValue) => { pParams.onValue({ value: pValue, name: 'leftOperand', instruction: pParams.instruction }); },
      [
        helpers.getExpressionValidationFunction(pParams.application.getParametersObject()),
        helpers.required,
      ],
      { labelIcon: $('<div class="ml_tooltip_icon ' + icons.unknown + '"/>').click(function() { tooltips.showTips('value'); })}
    ).widget;
    const lOperator = operator({
      label: 'operator',
      tooltip: 'Choose an operator to compare both operands',
      list: operatorOptionsList,
      initialValue: lConditionExpressionValues[OPERATOR],
      onValue: function (pValue) { pParams.onValue({ error: null, value: pValue, name: 'operator', instruction: pParams.instruction }); },
    });

    const lRightOperand = autocompleteComponent(
      'Value / Variable',
      'Operand value, a variable or a number',
      lConditionExpressionValues[RIGHT_OPERAND],
      pParams.application.listAllApplicationParameterNames(),
      (pValue) => { pParams.onValue({ value: pValue, name: 'rightOperand', instruction: pParams.instruction }); },
      [
        helpers.getExpressionValidationFunction(pParams.application.getParametersObject()),
        helpers.required,
      ],
      { labelIcon: $('<div class="ml_tooltip_icon ' + icons.unknown + '"/>').click(function() { tooltips.showTips('value'); })}
    ).widget;

    lLeftOperand.css({ visibility: isElseType ? 'hidden' : 'visible' });
    lOperator.css({ visibility: isElseType ? 'hidden' : 'visible' });
    lRightOperand.css({ visibility: isElseType ? 'hidden' : 'visible' });


    lInstructionListener = {
      // Handle undo/do mechanism
      onParameterChanged: function(pName, pValue, pId) {
        if (pName == 'conditionExpression' && ((pParams.hasOwnProperty('id') && pParams.id == pId) || !pParams.hasOwnProperty('id'))) {
          const lExpressionValues = getLeftOperatorRightFromExpression(pValue);
          lLeftOperand.find('input').val(lExpressionValues[LEFT_OPERAND]);
          lOperator.find('select').val(lExpressionValues[OPERATOR]);
          lRightOperand.find('input').val(lExpressionValues[RIGHT_OPERAND]);
        }
      }
    };
    pParams.instruction.addListener(lInstructionListener);

    pParams.container.on('remove', function onRemoveConditionExpression() {
      pParams.instruction.removeListener(lInstructionListener);
    });

    pParams.container.append(lLeftOperand, lOperator, lRightOperand);
  }

  /**
   * Footer for the condition instruction
   * Holds the button to add elseif, else statement
   * @param {instruction: lInstructionObject, addStatement: addStatement, conditionManager: lConditionManager} pParams
   * @param {ml.Instruction} pParams.instruction
   * @param {Function} pParams.addStatement
   * @param {ml.ConditionManager} pParams.conditionManager
   * @returns {JQuery}
   */
  function conditionFooter(pParams) {
    const lFooter = $('<footer class="ml_condition_footer"></footer>');
    const lElseIfButton = widgets.inlineButtonWithText(icons.plusRaw, 'Else if', function () {
      if (  pParams.instruction.mApp.listSequences().length == 1 ||
              ( pParams.instruction.mApp.listSequences().length == 2 && pParams.instruction.mApp.getInitialSequence().getName() != pParams.instruction.mSequence.getName() ) ) {
        widgets.messageBox('Add a child sequence before using this instruction category.', 'info');
        return;
      }
      pParams.addStatement(makeConditionByType({ instruction: pParams.instruction, type: 'elseIf' }));
    }).addClass('ml_inline_button').css({ 'min-width': '3.6rem' });
    const lElseButton = widgets.inlineButtonWithText(icons.plusRaw, 'Else', function () {
      if (  pParams.instruction.mApp.listSequences().length == 1 ||
              ( pParams.instruction.mApp.listSequences().length == 2 && pParams.instruction.mApp.getInitialSequence().getName() != pParams.instruction.mSequence.getName() ) ) {
        widgets.messageBox('Add a child sequence before using this instruction category.', 'info');
        return;
      }
      pParams.addStatement(makeConditionByType({ instruction: pParams.instruction, type: 'else' }));
    }).addClass('ml_inline_button').css({ 'min-width': '3.6rem' });

    const lConditionManagerListeners = {
      onConditionsChange: function (pHasElseStatement) {
        // we will prevent from adding `if-else` and `else` if no sequence are avalaible or if we already have an `else`
        const pApp = pParams.instruction.mApp;
        const pSeq = pParams.instruction.mSequence;
        const lHasNotEnoughSequences = pApp.listSequences().length == 1 || (pApp.listSequences().length == 2 && pApp.getInitialSequence().getName() != pSeq.getName());

        if (pHasElseStatement || lHasNotEnoughSequences) {
          lElseIfButton.attr('disabled', true).addClass('ml_button_bordered_disabled');
          lElseButton.attr('disabled', true).addClass('ml_button_bordered_disabled');
        }
        else {
          lElseIfButton.attr('disabled', false).removeClass('ml_button_bordered_disabled');
          lElseButton.attr('disabled', false).removeClass('ml_button_bordered_disabled');
        }
      }
    };

    pParams.conditionManager.addListener(lConditionManagerListeners);

    lFooter.on('remove', function onRemoveConditionFooter() {
      pParams.conditionManager.removeListener(lConditionManagerListeners);
    });

    lFooter.append(lElseIfButton, lElseButton);
    return lFooter;
  }

  /**
   * Add the widgets (keyword, operand, operators, etc)
   * for the condition statement
   * @param {Object} pParams Function arguments
   * @param {ml.Instruction} pParams.instruction
   * @param {ml.Sequence} pParams.sequence
   * @param {ml.Application} pParams.application
   * @param {ml.ConditionManager} pParams.conditionManager
   * @param { { instructionType: String, conditionExpression: String, execute: String, sequenceName: String} } pParams.parameter
   * @param {Number} pParams.id
   * @param {Function} pParams.onClose
   * @returns {JQuery} Jquery element
   */
  function conditionalStatement(pParams) {
    const lConditionalOuterLayout = $('<div class="ml_conditional_grid" />');
    const lApplication = pParams.application;
    const lApplicationParametersObject = lApplication.getParametersObject();
    const lInstructionParam = pParams.parameter;
    const lInstruction = pParams.instruction;
    const lInstructionModel = lInstruction._getInstructionModel();
    const lStatementLayout = statement("THEN");
    const lStatementInnerContainer = lStatementLayout.getInnerContainer();
    const lRemoveButton = $('<button class="ml_close ml_button_condition_remove"></button>');
    let lInstructionListener = {};
    const lParamsMap = {};

    lInstructionModel.parameters[0].unit.forEach(function (parameterObj) {
      let lInitialValue = lInstructionParam[parameterObj.name];
      switch (parameterObj.name) {
        case 'instructionType': {
          lInitialValue = lInitialValue.split(/(?=[A-Z])/).join(' ');
          const _lConditionLayout = keywordPlacehold({ label: 'condition', type: lInitialValue, class: 'ml_if_widget' });
          lConditionalOuterLayout.append(_lConditionLayout);
          const _lIcon = $(
            '<div class="ml_tooltip_icon ml_label_icon ' + icons.unknown + '"/>'
          ).click(function () {
            tooltips.showTips('if');
          });
          const _lExistingIcon = _lConditionLayout.find('.ml_label_icon');
          if (_lExistingIcon.length) {
            _lExistingIcon.replaceWith(_lIcon);
          } else {
            _lConditionLayout.find('.h_layout').append(_lIcon);
          }
          break;
        }
        case 'conditionExpression': {
          // TODO: this just consider the expession like "value OPERATOR value" "1 == 1"
          conditionExpression({
            application: lApplication,
            container: lConditionalOuterLayout,
            instruction: lInstruction,
            instructionParam: lInstructionParam,
            expression: lInitialValue,
            id: pParams.id,
            onValue: updateConditionExpressionValues({ conditionManager: pParams.conditionManager, id: pParams.id, instruction: lInstruction }),

          });
          break;
        }
        case 'execute': {
          const lExecuteWidget = widgets.listWidget(parameterObj.name, parameterObj.values, 'Execution Type', lInitialValue, function (pValue) {
            pParams.conditionManager.setParameter({ id: pParams.id, name: 'execute', value: pValue, instruction: lInstruction });
          }).css({ 'flex': '1' });
          const execute_map = {'inSeries': 'execute', 'inParallel': 'call'};
          lExecuteWidget.setLabelIcon(
            $('<div class="ml_tooltip_icon ' + icons.unknown + '"/>').click(function() {
              tooltips.showTips(execute_map[lExecuteWidget.getValue()]);
            })
          );
          lParamsMap['execute'] = lExecuteWidget;
          lStatementInnerContainer.append(lExecuteWidget);
          break;
        }
        case 'sequenceName': {
          const lSequenceNameWidget = sequenceListWidget(parameterObj.name, pParams.application, pParams.sequence, 'The sequence to be invoked', lInitialValue, function (pValue) {
            pParams.conditionManager.setParameter({ id: pParams.id, name: 'sequenceName', value: pValue, instruction: lInstruction });
          }).css({ 'flex': '1' });
          lParamsMap['sequenceName'] = lSequenceNameWidget;
          lStatementInnerContainer.append(lSequenceNameWidget);
          break;
        }
        default: {
          console.warn('Instruction parameter ' + parameterObj.name + ' not handled.');
          break;
        }
      }
    });

    if (pParams.onClose) {
      lRemoveButton.on('click', pParams.onClose);
      lConditionalOuterLayout.append(lRemoveButton);
    }

    lInstructionListener = {
      onParameterChanged: function(pName, pValue, pId) {
        if (lParamsMap.hasOwnProperty(pName) && pId == pParams.id) {
          lParamsMap[pName].find('select').val(pValue);
        }
      }
    };

    pParams.instruction.addListener(lInstructionListener);

    lConditionalOuterLayout.on('remove', function onRemoveConditionStatement() {
      pParams.instruction.removeListener(lInstructionListener);
    });

    lConditionalOuterLayout.append(lStatementLayout);
    return lConditionalOuterLayout;
  }

  /**
   * Built a conditional
   * instruction layout
   * @param {Object} pParams
   * @param {String} pParams.icon - Icon class name
   * @param {ml.Instruction} pParams.instruction - Machine Logic Instruction object
   * @param {ml.Sequence} pParams.sequence - Machine Logic Sequence object
   * @returns {JQuery} JQuery element
   */
  function conditionalLayout(pParams) {
    const lContainer = box().addClass('bordered spaced');
    const lInstructionLayout = $('<div class="h_layout"> </div>');
    const lSequence = pParams.sequence;
    const lInstruction = pParams.instruction;

    lInstructionLayout.closeButton = closeButton(function () {
      lSequence.removeInstruction(lSequence.getInstructionIndex(lInstruction));
      lInstructionLayout.remove();
    });
    // instruction number "id"
    lInstructionLayout.identification = instructionIdentification(pParams.icon);
    lInstructionLayout.body = instructionBody();
    lInstructionLayout.append([lInstructionLayout.closeButton, lInstructionLayout.identification, lInstructionLayout.body]);

    lContainer.body = lInstructionLayout.body;
    lContainer.append(lInstructionLayout, $('<div class="ml_animated_background"/>'));
    return lContainer;
  }

  /**
   *
   * @param {Object} pParams
   * @param {Error} pParams.error
   * @param {ml.Instruction} pParams.instruction - Machine Logic InstructionObject
   * @param {String} pParams.name - operator/operand name
   * @param {String} pParams.value - value on widget
   */
  function _constructExpression (pParams) {
    const lError = pParams.error; const lValue = pParams.value;
    const lName = pParams.name; const lInstruction = pParams.instruction;
    if (lError) {
      widgets.messageBox(lError.message + '. Your changes have been reverted.', 'warning');
      return;
    }

    const lExpression = lInstruction.getParameter('conditionExpression');
    const lExpressionValues = getLeftOperatorRightFromExpression(lExpression);
    let lField;

    if (lName === 'leftOperand')        { lField = LEFT_OPERAND; }
    else if (lName === 'operator')      { lField = OPERATOR; }
    else if (lName === 'rightOperand')  { lField = RIGHT_OPERAND; }

    lExpressionValues[lField] = lValue;
    lInstruction.setParameter('conditionExpression', lExpressionValues.join(' '));
  }

  /**
   * Construct a close button
   * @param {Function} pOnClose - Function called on click
   * @returns {JQuery} JQuery element
   */
  function closeButton(pOnClose) {
    const lCloseButton = $('<button class="ml_close ml_close_card_widget"/>').css({ 'margin-right': '0.45rem' });
    if (pOnClose) { lCloseButton.click(pOnClose); }
    return lCloseButton;
  }

  /**
  * An expression field
  * @param {string} pName - Used as id
  * @param {Application} pApplication - The Application object
  * @param {string} pTooltip - Should as tooltip when hovered
  * @param {string} pUnits - The unit type for the widget (mm or mm/s or mm/s^2)
  * @param {string} pValue - The initial value of the expression when creating the widget
  * @param {function} pOnValue - value triggered when change value. This callback has nodeJS style callback(err, data)
  * @deprecated - no longer supported, do not use. use autocompleteComponent with helpers.getExpressionValidationFunction,
  */

  function expressionWidget(pName, pApplication, pTooltip, pUnits, pValue, pOnValue, pValidationFunctions, pOptions) {
    console.warn('The expressionWidget widget is deprecated and no longer supported, using it might lead to unintend behaviour');
    pOptions.configManager = pApplication.mLibrary.getConfigManager();
    pOptions.pApp = pApplication;
    const pInitialSource = pApplication.getParametersObject().listParametersNameOfType(['const', 'input', 'lambda', 'unpackConst']);
    const lValidationFunctions = [helpers.getExpressionValidationFunction(pApplication.getParametersObject()), ...pValidationFunctions];
    const _lWidget = widgets.autocompleteWidget(pName, pTooltip, pUnits, pOptions, pInitialSource, lValidationFunctions, pValue, pOnValue);

    return _lWidget;
  }

  /**
  * An expression field
  * @param {string} pName - Used as id
  * @param {Application} pApplication - The Application object
  * @param {string} pTooltip - Should as tooltip when hovered
  * @param {string} pValue - The initial value of the expression when creating the widget
  * @param {function} pOnValue - value triggered when change value. This callback has nodeJS style callback(err, data)
  * @deprecated - no longer supported, do not use. use autocompleteComponent with custom validation function,
  */
  function variableNameWidget(pName, pApplication, pTooltip, pValue, pOnValue) {
    console.warn('The variableNameWidget widget is deprecated and no longer supported, using it might lead to unintend behaviour');
    const pOptions = {pConfig: pApplication.mLibrary.getConfigManager() , pApp: pApplication};
    const pInitialSource = pApplication.getParametersObject().listParametersNameOfType(['const']);
    const pValidationFunctions = [helpers.required, helpers.validateVariableNames(pApplication.getParametersObject())];

    const _lWidget = widgets.autocompleteWidget(pName, pTooltip, null, pOptions, pInitialSource, pValidationFunctions, pValue, pOnValue);

    return _lWidget;
  }


  /**
   * Construct float parameter widget
   * @param {Object} pParams
   * @param {Object} pParams.application - Machine logic application object
   * @param {Object} pParams.applicationParameterObject - Machine logic application parameter object
   * @param {String} pParams.axisUnit - axis unit mm or deg
   * @param {Object} pParams.instruction - Machine logic instruction object
   * @param {Object} pParams.library - Machine logic library object
   * @param {String} pParams.parameter - Parameter name
   * @param {Object} pParams.parameterInfo - Parameter properties such as: description, name, unit, format, etc
   * @returns {jQuery} jQuery object
   */

  /**
   * @deprecated - this widget is no longer supported avoid using it.
   */
  function floatParamWidget(pParams) {
    console.warn('The floatParamWidget widget is deprecated and no longer supported, using it might lead to unintend behaviour');
    const lApp = pParams.application;
    const lApplicationParameterObject = pParams.applicationParameterObject;
    const lAxisUnit = pParams.axisUnit;
    const lInstruction = pParams.instruction;
    const lLibrary = pParams.library;
    const lParameter = pParams.parameter;
    const lParameterInfo = pParams.parameterInfo;
    let lUnit = lParameterInfo.unit;
    const lParameterValueFloat = lInstruction.getParameter(lParameter);
    const lValueToSetFloat = isNaN(lParameterValueFloat) ? lParameterValueFloat : parseFloat(lParameterValueFloat);
    let lAfterUnit = "";
    const AUTOCOMPLETE_WIDGET_NAME = lParameterInfo.name + PARAMETER_LABEL_SUFIX;
    const lOptions = {step: 0.1};
    const getMinMaxValidationFunction = helpers.getMinMaxValidationFunction({ 'min': lParameterInfo.minimum, 'max': lParameterInfo.maximum });

    function callbackOnValueFloat(pError, pValue) {
      if (pError) {
        widgets.messageBox(pError.message + '. Your changes have been reverted.', 'warning');
      } else {
        lInstruction.setParameter(lParameter, isNaN(pValue) ? pValue : parseFloat(pValue));
      }
    }

    if (lParameterInfo.hasOwnProperty('acceptedVariableTypes')) { lOptions.pApp = lApp; }
    // Define unit
    if (lParameterInfo.unit.includes("axisDependant")) {
      lUnit = lAxisUnit || "mm";
      if (lParameterInfo.unit.includes("Velocity")) {
        lUnit = lUnit + "/s";
      }
      else if (lParameterInfo.unit.includes("Acceleration")) {
        lUnit = lUnit + "/s^2";
      }
    }
    else if (lParameterInfo.unit.includes("configDependant")) {
      lOptions['configManager'] = lLibrary.getConfigManager();
      lParameterInfo.needAdditionalDisplay = true;
      if (lParameterInfo.unit == "configDependantVelocity") {
        lAfterUnit = "deg/s";
      }
      else if (lParameterInfo.unit == "configDependantAcceleration") {
        lAfterUnit = "deg/s<sup>2</sup>";
      }
      lOptions.needAdditionalDisplay = true;
      lOptions.additionalDisplay = {
        before: "≡ ",
        value: parseFloat(lInstruction.getParameter(lParameter)),
        after: " " + lAfterUnit,
      };
    }
    return lParameterInfo.hasOwnProperty('acceptedVariableTypes') ?
      expressionWidget(AUTOCOMPLETE_WIDGET_NAME, lApp, lParameterInfo.description, lUnit, lValueToSetFloat, callbackOnValueFloat, [helpers.required, getMinMaxValidationFunction], lOptions)
      :
      widgets.numberWidget(lParameterInfo.name, lParameterInfo.description, lValueToSetFloat, lUnit, lOptions, function(pValue) { lInstruction.setParameter(lParameter, isNaN(pValue) ? pValue : parseFloat(pValue)); });
  }

  /**
   * Construct an integer parameter widget
   * @param {Object} pParams
   * @param {Object} pParams.application - Machine logic application object
   * @param {Object} pParams.applicationParameterObject - Machine logic application parameter object
   * @param {Object} pParams.instruction - Machine logic instruction object
   * @param {String} pParams.parameter - Parameter Name
   * @param {Object} pParams.parameterInfo - Parameter properties such as: description, name, unit, format, etc
   * @returns {jQuery} jQuery object
   * @deprecated - this widget is no longer supported avoid using it.
   */
  function integerParamWidget(pParams) {
    console.warn('The integerParamWidget widget is deprecated and no longer supported, using it might lead to unintend behaviour');
    const lApp = pParams.application;
    const lApplicationParameterObject = pParams.applicationParameterObject;
    const lInstruction = pParams.instruction;
    const lParameter = pParams.parameter;
    const lParameterInfo = pParams.parameterInfo;
    const lParameterValueInteger = lInstruction.getParameter(lParameter);
    const lValueToSetInteger = isNaN(lParameterValueInteger) ? lParameterValueInteger : parseInt(lParameterValueInteger);
    const AUTOCOMPLETE_WIDGET_NAME = lParameterInfo.name + PARAMETER_LABEL_SUFIX;
    const lOptions = {};
    const getMinMaxValidationFunction = helpers.getMinMaxValidationFunction({ 'min': lParameterInfo.minimum, 'max': lParameterInfo.maximum });

    function callbackOnValueInteger(pError, pValue) {
      if (pError) {
        widgets.messageBox(pError.message + '. Your changes have been reverted.', 'warning');
      } else {
        lInstruction.setParameter(lParameter, isNaN(pValue) ? pValue : parseInt(pValue));
      }
    }

    if (lParameterInfo.hasOwnProperty('acceptedVariableTypes')) {
      lOptions.pApp = lApp;
    }

    return lParameterInfo.hasOwnProperty('acceptedVariableTypes') ?
      widgets.autocompleteWidget(AUTOCOMPLETE_WIDGET_NAME, lParameterInfo.description, lParameterInfo.unit, lOptions, lApplicationParameterObject.listParametersNameOfType(lParameterInfo.acceptedVariableTypes), [helpers.required, getMinMaxValidationFunction], lValueToSetInteger, callbackOnValueInteger)
      :
      widgets.numberWidget(lParameterInfo.name, lParameterInfo.description, lValueToSetInteger, lParameterInfo.unit, lOptions, function(pValue) { lInstruction.setParameter(lParameter, isNaN(pValue) ? pValue : parseInt(pValue)); });
  }

  /**
  * Build a flex-box direction
  * column with a width 100%
  * for append the conditional
  * instruction layout body
  * @returns {JQuery} JQuery element
  */
  function instructionBody() {
    return $('<div class="v_layout"></div>').css({ 'width': '100%' });
  }

  /**
   * Widget with a flex-box
   * direction column to show
   * the instruction number and icon
   * @param {String} pIcon - Icon class name for the instruction
   * @returns {JQuery} JQuery Element
   */
  function instructionIdentification(pIcon) {
    const lIdentification = $('<div class="v_layout spaced ml_icon_instruction_widget" />').css({ 'margin-right': '0.25rem', flex: '1 1 30px' });
    // Id number is added on Sequence Document, queried by the class ml_number
    const lIdNumber = $('<div class="h_layout"> <h5 class="ml_widget_label ml_number"/> </div>');
    const lIcon = $('<div class="' + pIcon + '" />');

    lIdentification.append(lIdNumber, lIcon);
    return lIdentification;
  }

  /**
   * Static container with a keyword
   * @param {Object} pParams
   * @param {String} pParams.type
   * @param {String} pParams.label
   * @param {String} pParams.class
   * @returns {JQuery} JQuery element
   */
  function keywordPlacehold(pParams) {
    const lLabel = pParams.label; const lType = pParams.type; const lClass = pParams.class;
    const lBase = widgets.baseWidget(lLabel).addClass('v_layout spaced');
    const lDiv = $('<div class="ml_parameter_widget ' + lClass + '">' + lType + ' </div>');

    lBase.append(lDiv);

    return lBase;
  }

  /**
   * Widget to layout
   * a loop instruction
   * @param {Object} pParams
   * @param {ml.Application} pParams.application - Machine logic Application Object
   * @param {ml.Instruction} pParams.instruction - Machine logic Instruction Object
   * @param {Object} pParams.parameter - Instruction parameters
   * @param {String} [pParams.parameter.name=count|conditionExpression]
   * @param {String} [pParams.parameter.execute=inSeries]
   * @param {String} pParams.parameter.sequenceName
   * @param {ml.Sequence} pParams.sequence - Machine logic Sequence Object
   * @returns {JQuery} Jquery element
   */
  function logicalExpression_loop(pParams) {
    const lContainer = $('<div class="v_layout" />').css({ 'margin-bottom': '0.25rem' });
    const lInstruction = pParams.instruction;
    const lConditionalStatement = loopStatement(pParams);

    const _lInstructionListener = {
      onNameChanged: function (/*pName*/) {
        pParams.parameter = pParams.instruction.mParameters;
        // Detach all the parameters and build the instruction card again
        lContainer.children().detach();
        lContainer.append(loopStatement(pParams));
      }
    };
    lInstruction.addListener(_lInstructionListener);

    lContainer.on('remove', function onRemoveLogicalExpression() {
      lInstruction.removeListener(_lInstructionListener);
    });

    lContainer.append(lConditionalStatement);
    return lContainer;
  }

  /**
   *
   * @param {Object} pParams
   * @param {ml.Application} pParams.application - Machine logic Application Object
   * @param {ml.ConditionManager} pParams.conditionManager - Machine logic ConditionManager Object
   * @param {ml.Instruction} pParams.instruction - Machine logic Instruction Object
   * @param {Object} pParams.parameter - Instructions Parameters
   * @param {String} [pParams.parameter.instructionType=if|elseIf|else ] - Condition keyword
   * @param {String} [pParams.parameter.conditionExpression= 10 == 10] - Expression to the condition
   * @param {String} [pParams.parameter.execute=inSeries|inParallel] - Which type the sequence should run
   * @param {String} pParams.parameter.sequenceName - Which type the sequence should run
   * @param {Function} pParams.onClose - Function called when close button of a elseif, else is clicked
   * @param {ml.Sequence} pParams.sequence - Machine logic Sequence Object
   */
  function logicalExpression(pParams) {
    // This is a statement(if, elseIf, else) container
    const lContainer = $('<div class="v_layout" />').css({ 'margin-bottom': '0.5rem' });
    const lConditionalStatement = conditionalStatement(pParams);

    lContainer.append(lConditionalStatement);
    return lContainer;
  }

  /**
  * Create loop widget
  * @param {Object} pParams
  * @param {ml.Library} pParams.library - Machine Logic Library Object
  * @param {ml.Application} pParams.application - Machine Logic Application Object
  * @param {ml.Sequence} pParams.sequence - Machine Logic Sequence Object
  * @param {ml.instruction} pParams.instruction - Machine Logic Instruction Object
  * @return {JQuery} Jquery element
  */
  function loop(pParams) {
    const lContainer = conditionalLayout({ sequence: pParams.sequence, instruction: pParams.instruction, icon: icons.loopNew });
    const lInstructionObject = pParams.instruction;
    const lApplicationObject = pParams.application;

    lContainer.body.append(logicalExpression_loop({ instruction: lInstructionObject, sequence: pParams.sequence, parameter: lInstructionObject.mParameters, application: lApplicationObject }));
    return lContainer;
  }

  /**
  * Widget that add children
  * components to build the
  * loop instruction
  * @param {Object} pParams
  * @param {ml.Application} pParams.application - Machine logic Application Object
  * @param {ml.Instruction} pParams.instruction - Machine logic Instruction Object
  * @param {Object} pParams.parameter - Instruction parameters
  * @param {String} [pParams.parameter.name=count|conditionExpression]
  * @param {String} [pParams.parameter.execute=inSeries]
  * @param {String} pParams.parameter.sequenceName
  * @param {ml.Sequence} pParams.sequence - Machine logic Sequence Object
  * @returns {JQuery} Jquery element
  */
  function loopStatement(pParams) {
    const lConditionalOuterLayout = $('<div class="ml_conditional_grid" />');
    const lApplication = pParams.application;
    const lInstructionParam = pParams.parameter;
    const lInstruction = pParams.instruction;
    const lInstructionModel = lInstruction._getInstructionModel();
    const lStatementLayout = statement("DO");
    const lStatementInnerContainer = lStatementLayout.getInnerContainer();
    const lRemoveButton = $('<button class="ml_close ml_button_condition_remove"></button>');
    const lParamsMap = {};

    // add the instruction name first
    const lReplaceCategory = lInstruction.getCategory();
    const _lFilteredActionsSet = utils.getActions(lApplication.mLibrary, lInstruction);
    const _onAction = function (pAction) {
      lInstruction.setName(pAction);
    };
    const _lActions = widgets.listWidget(lReplaceCategory, utils.getActionList(_lFilteredActionsSet), 'Action to be perfomed', lInstruction.getName(), _onAction);

    _lActions.find('.ml_select_widget').addClass('ml_loop_widget').css({ 'min-width': 'unset' });
    lConditionalOuterLayout.append(_lActions);

    lInstructionModel.parameters.forEach(function (parameterObj) {
      const lInitialValue = lInstructionParam[parameterObj.name];
      const _lApplicationParameterObject = lApplication.getParametersObject();

      const isValidParameter = _lApplicationParameterObject.isValidParameter.bind(_lApplicationParameterObject);

      switch (parameterObj.name) {
        case 'count': {
          const lCountWidget = autocompleteComponent(
            parameterObj.name,
            parameterObj.description,
            lInstruction.getParameter(parameterObj.name),
            lApplication.listAllApplicationParameterNames(),
            (pValue) => { lInstruction.setParameter(parameterObj.name, pValue); },
            [
              helpers.getExpressionValidationFunction(lApplication.getParametersObject()),
              helpers.required,
              helpers.getMinMaxValidationFunction({ 'min': parameterObj.minimum, 'max': Number.MAX_SAFE_INTEGER })
            ],
            { getUnit: () => { return 'times'; }}
          ).widget;
          lParamsMap['count'] = lCountWidget;
          lConditionalOuterLayout.append(lCountWidget);
          break;
        }
        case 'conditionExpression': {
          // TODO: this just consider the expression like "value OPERATOR value" "1 == 1"
          conditionExpression({
            application: lApplication,
            autocompleteSource: _lApplicationParameterObject.listParametersName(),
            container: lConditionalOuterLayout,
            expression: lInitialValue,
            instruction: lInstruction,
            onValue: _constructExpression,
            validationFunctions: [helpers.required, isValidParameter],
          });
          break;
        }
        case 'execute': {
          lStatementInnerContainer.append(keywordPlacehold({ label: 'Execute', type: "In Series", class: 'ml_loop_execute_widget' }).css({ 'flex': '1', 'line-height': '1.5rem' }));
          break;
        }
        case 'sequenceName': {
          const lSequenceNameWidget = sequenceListWidget(parameterObj.name, pParams.application, pParams.sequence, 'The sequence to be invoked', lInstruction.getParameter(parameterObj.name), function (pValue) {
            lInstruction.setParameter(parameterObj.name, pValue);
          }).css({ 'flex': '1' });
          lParamsMap['sequenceName'] = lSequenceNameWidget;
          lStatementInnerContainer.append(lSequenceNameWidget);
          break;
        }
        default: {
          console.warn('Instruction parameter ' + parameterObj.name + ' not handled.');
          break;
        }
      }
    });

    // Append tooltip icon to label
    const lFirstLabel = lConditionalOuterLayout.last().find('.h_layout');
    if (lFirstLabel) {
      lFirstLabel.first().append(
        $('<div class="ml_tooltip_icon ml_label_icon ' + icons.unknown + '"/>').click(
          function () {
            tooltips.showTips(lInstruction.getName());
          }
        )
      );
    }

    if (pParams.onClose) {
      lRemoveButton.on('click', pParams.onClose);
      lConditionalOuterLayout.append(lRemoveButton);
    }

    const lInstructionListener = {
      onParameterChanged: function(pName, pValue) {
        if (lParamsMap.hasOwnProperty(pName)) {
          lParamsMap[pName].find('input, select').val(pValue);
        }
      }
    };

    pParams.instruction.addListener(lInstructionListener);

    lConditionalOuterLayout.on('remove', function onRemoveLoop() {
      pParams.instruction.removeListener(lInstructionListener);
    });

    lConditionalOuterLayout.append(lStatementLayout);
    return lConditionalOuterLayout;
  }

  /**
  * Create a default payload
  * for conditional statement by
  * the condition keyword
  * @param {Object} pParams
  * @param {String} pParams.type
  * @param {ml.Instruction} pParams.instruction
  * @returns { instructionType: String, conditionExpression: String, execute: String, sequenceName: String } Default condition statement
  */
  function makeConditionByType(pParams) {
    const lInstructionModel = pParams.instruction._getInstructionModel();
    const lDefaultValue = Object.assign({}, lInstructionModel.parameters[0].default[0]);
    if ('else' === pParams.type) {
      lDefaultValue['conditionExpression'] = '';
    }

    lDefaultValue.instructionType = pParams.type;
    return lDefaultValue;
  }

  /**
   * Wrapper of a select element
   * to display a drop-down list with operators
   * @param {Object} pParams
   * @param {String} pParams.initialValue
   * @param {String} pParams.label
   * @param {String[]} pParams.list
   * @param {Function} pParams.onValue
   * @returns {JQuery} Jquery Element
   */
  function operator(pParams) {
    const lOperator = widgets.listWidget(pParams.label, pParams.list, pParams.tooltip, pParams.initialValue, pParams.onValue);
    const lSelectParent = lOperator.getSelect().parent();
    lSelectParent.css({ 'min-width': '4rem' });

    return lOperator;
  }

  /**
   * Create a layout with a keyword
   * and section with different background to place
   * widgets
   * @param {String} keyword
   * @returns {JQuery} Jquery element
   */
  function statement(keyword) {
    const lStatementOutterContainer = $('<div class="ml_condition_statement ml_condition_statement_layout" />');
    const lKeyword = $('<h3> ' + keyword + ' </h3>').css({ 'align-self': 'center', 'font-size': '1rem' });
    const lStatementInnerContainer = $('<div class="h_layout ml_execute_statement_layout"> </div>');
    const lIcon = $('<div class="' + icons.trigger + '" />').css({ 'align-self': 'flex-end', 'font-size': '1.5rem', padding: '0 0.25rem 0.25rem 0' });
    const getInnerContainer = function getInnerContainer() {
      return lStatementInnerContainer;
    };

    lStatementInnerContainer.append(lIcon);
    lStatementOutterContainer.append(lKeyword, lStatementInnerContainer);

    lStatementOutterContainer.getInnerContainer = getInnerContainer;

    return lStatementOutterContainer;
  }

  /**
   * Drop-down list with the
   * child sequence names
   * @param {String} pName - Label used for the widget
   * @param {ml.Application} pApp - Machine Logic Application Object
   * @param {ml.Sequence} pSequence - Machine Logic Sequence Object
   * @param {String} pTooltip - Tooltip showed on hovering the element
   * @param {String} pValue - Initial Value
   * @param {Function} pOnValue - Function called onSelect
   * @param {String} pInstructionType - Instruction type
   * @param {Boolean} pAllowEmpty - Allow empty value
   * @param {String} pEmptyReplacementText - Text to be displayed when empty
   * @param {Object} [pOptions] - Options
   * @param {String} [pOptions.tooltipKey] - Tooltip key
   * @returns {JQuery} JQuery element
   */
  function sequenceListWidget(pName, pApp, pSequence, pTooltip, pValue, pOnValue, pInstructionType, pAllowEmpty, pEmptyReplacementText, {tooltipKey = undefined} = {}) {
    // if a selection is made, and it was (missing sequence), we will remove it from the options
    function onValue(pNewValue) {
      _lWidget.find('option:contains("(Missing Sequence)"),option[disabled="disabled"]').remove();
      if(pOnValue) { pOnValue(pNewValue); }
    }

    const _lWidget = widgets.listWidget(pName, [], pTooltip, pValue, onValue, false, false, {tooltipKey});
    const _lSelect = _lWidget.getSelect();

    function _addOption(pOtherSequence) {
      const _lOption = $('<option value="' + pOtherSequence.getName() + '">' + pOtherSequence.getName() + '</option>');
      _lSelect.append(_lOption);
    }

    let lSeqExists = false;
    pApp.listSequences().filter(function (pSeqName) {
      const lSequenceIsUnlocked = !pApp.getSequence(pSeqName).getIsLocked();

      if(pSeqName === pValue || (pAllowEmpty && "" === pValue)) { lSeqExists = true; }

      if (pInstructionType && pInstructionType === 'call') {
        const sequenceObj = pApp.mSequences.find(seq => seq.mName === pSeqName);
        // Filter out the current sequence itself and the main sequence as well
        // if pInstructionType call
        // and sequence has a robotSequenceInstruction
        // don't add as option
        return pSeqName != pSequence.getName() && pSeqName != pApp.getInitialSequence().getName() && lSequenceIsUnlocked;
      }
      else if (pApp.mIsMachineApp && pSequence.getName() == pApp.getInitialSequence().getName()) {
        //let the machineapp main sequence show the locked sequences in drop downdown
        return pSeqName != pSequence.getName() && pSeqName != pApp.getInitialSequence().getName();
      }
      else {
        return pSeqName != pSequence.getName() && pSeqName != pApp.getInitialSequence().getName() && lSequenceIsUnlocked;
      }
    }).forEach(function (pSeqName) {
      _addOption(pApp.getSequence(pSeqName));
    });

    if (pAllowEmpty) {
      // not using _addOption because it does not offer the ability to have a different value from the option text
      const lOption = $(`<option value="">${pEmptyReplacementText || ""}</option>`);
      _lSelect.append(lOption);
    }

    if (pValue == "" && !pAllowEmpty){
      // if empty String, initialize widget to first option (creation)
      _lSelect.prop("selectedIndex", 0);
      pOnValue(_lWidget.getValue());

    } else {
      if(!lSeqExists) {
        // if the sequence does not exist we will show (missing sequence) instead of the name, the real name will be used later to find it
        const _lOption = $(`<option value="${pValue}" disabled> (Missing Sequence) </option>`);
        _lSelect.append(_lOption);
        _lSelect.addClass('ml_validator_red');

        // This is to remove the RED on invalid option when the drop down is openeded
        // NOTE: so apparently, _lSelect.val() will be null if the option is disabled... that is unfortunate....
        _lSelect.on('click', function() { _lSelect.removeClass('ml_validator_red'); });
        _lSelect.on('focusout', function() { if(_lSelect[0].value == pValue) { _lSelect.addClass('ml_validator_red'); } });
        _lSelect.on('keydown', function(pEvent) { if(pEvent.keyCode == 40) { _lSelect.removeClass('ml_validator_red'); } });
      }
      _lWidget.setValue(pValue);
    }

    // works in the same way that the above logic does but that only runs when the widget is rendered.
    // this is needed if a child sequence is deleted while still in the parent sequence.
    const addOrRemoveMissingSequenceOption = (pDeletedSequence) => {
      const lDeletedSequenceName = pDeletedSequence.getName();
      const lOption = _lSelect.find(`option[value="${lDeletedSequenceName}"]`);
      // if the deleted option is NOT selected, just remove it from the list
      if(lOption.val() !== _lSelect.val()) { return lOption.remove(); }

      // if the current selected option is the deleted option, change its name and add some error styling
      // if the sequence does not exist we will show (missing sequence) instead of the name, the real name will be used later to find it
      lOption
        .text('(Missing Sequence)')
        .attr('disabled', 'disabled');

      // add the invalid class
      _lSelect.addClass('ml_validator_red');

      // This is to remove the RED on invalid option when the dropdown is opened
      _lSelect.on('click', function() { _lSelect.removeClass('ml_validator_red'); });
      _lSelect.on('focusout', function() { if(_lSelect[0].value === lDeletedSequenceName) { _lSelect.addClass('ml_validator_red'); } });
      _lSelect.on('keydown', function(pEvent) { if(pEvent.keyCode == 40) { _lSelect.removeClass('ml_validator_red'); } });
    };

    const _lAppListener = {
      onSequenceAdded: _addOption,
      onSequenceRemoved: addOrRemoveMissingSequenceOption
    };

    pApp.addListener(_lAppListener);

    _lWidget.on('remove', function onRemoveSequenceListWidget() {
      pApp.removeListener(_lAppListener);
    });

    return _lWidget;
  }

  /**
  * Callback function to update
  * The parameters for the condition
  * statement
  * @param {Object} pParamsClosure arguments to the closure function
  * @param {Number} pParamsClosure.id unique id for instruction condition
  * @param {ml.ConditionManager} pParamsClosure.conditionManager condition manager object
  */
  function updateConditionExpressionValues(pParamsClosure) {
    /**
     * @param {Object} pParams
     * @param {String} pParams.name
     * @param {String} pParams.value
     */
    return function (pParams) {
      pParamsClosure.conditionManager.setParameter({ id: pParamsClosure.id, name: pParams.name, value: pParams.value, instruction: pParamsClosure.instruction });
    };
  }

  /**
   * Drop-down list to assign
   * value for an variable
   * @param {String} pName - Label used for the widget
   * @param {ml.Application} pApp - Machine Logic Application Object
   * @param {String} pTooltip - Tooltip on hovering the element
   * @param {String} pValue - Initial Value
   * @param {Function} pOnValue - Callback function on select
   * @returns {JQuery} JQuery element
   */
  function variableListWidget(pName, pApp, pTooltip, pValue, pOnValue) {
    const _lWidget = widgets.listWidget(pName, [], pTooltip, pValue, pOnValue);
    const _lSelect = _lWidget.getSelect();

    pApp.getParametersObject().listParametersNameOfType(['const']).forEach(function (pParamName) {
      const _lOption = $('<option value="' + pParamName + '">' + pParamName + '</option>');
      _lSelect.append(_lOption);
    });

    // Initial value
    if (pValue != "") { // not default empty value
      _lSelect.val(pValue);
    } else {            // default empty value
      pOnValue(_lWidget.getValue());
    }

    return _lWidget;
  }

  /**
   * Template text box
   * @param {String} pName - Label used for the widget
   * @param {ml.Application} pApp - Machine Logic Application Object
   * @param {String} pTooltip - Tooltip on hovering the element
   * @param {String} pValue - Initial Value
   * @param {Function} pOnValue - Callback function on select
   * @returns {JQuery} JQuery element
   */
  function logMessageWidget(pName, pApp, pTooltip, pValue, pOnValue) {
    const _lWidget = widgets.baseWidget(pName).css('grid-column', '1 / -1');
    const _lWidgetContainer = $('<div>').addClass('ml_parameter_widget ml_log_message_container h_layout spaced');
    const _lStripValueOfInvalidStrings = function (pValue) {
      // Note (mkosarek) Does the following: If we match ${WORD}, this regex replaces it with WORD
      return pValue.replace(/\${(.+?)\}/g, "$1").replace(/`/g, '');
    };

    const _lProcessValue = function (pPlainText) {
      const _lContent = _lStripValueOfInvalidStrings(pPlainText);
      const _lParameters = pApp.getParametersObject();
      let _lNewContent = [];
      let _lNewSavedValue = [];
      const _lWords = _lContent.split(' ');

      for (const word of _lWords) {
        const cleanWord = word.replaceAll("[^a-zA-Z0-9()]", "");

        //Logic for lambda functions
        if (word.includes("(") && word.includes(")") && (cleanWord.substring(0, cleanWord.indexOf("("))) != "") {
          const parameter = pApp.getParameterObjectByName(cleanWord.substring(0, cleanWord.indexOf("(")));
          if (parameter && parameter.type == 'lambda') {
            _lNewSavedValue.push("${" + cleanWord + "}");
            _lNewContent.push("<b>" + word + "</b>");
          } else {
            _lNewSavedValue.push(word);
            _lNewContent.push(word);
          }
        }
        //Logic for variables (const and input)
        else if (_lParameters.hasParameter(cleanWord, true)) {
          _lNewSavedValue.push("${" + word + "}");
          _lNewContent.push("<b>" + word + "</b>");
        } else {
          _lNewSavedValue.push(word);
          _lNewContent.push(word);
        }
      }

      _lNewSavedValue = _lNewSavedValue.join(' ');
      _lNewContent = _lNewContent.join(' ');

      return { savedValue: '`' + _lNewSavedValue + '`', displayedValue: _lNewContent };
    };

    const _lValToHtml = function (pText) {
      return _lProcessValue(_lStripValueOfInvalidStrings(pText)).displayedValue;
    };

    const _lContentEditor = $('<div>')
      .attr('contenteditable', 'true')
      .attr('title', pTooltip)
      .addClass('ml_log_message_box')
      .html(_lValToHtml(pValue));

    // The VirtualKeyboard works with inputs and textareas
    // Here, we cannot use any of these because we want to do special formatting (highlight variable names in orange)
    // For the VirtualKeyboard to work on our contenteditable div, we'll use a backing textarea
    //
    // This textarea should reflect the state of the contenteditable:
    // - Send a focus event when the contenteditable is focused
    // - Send a blur event when the contenteditable is blurred
    // - Send input events when an input happens on the contenteditable
    // - Have the proper caret position
    // - Have the proper value
    //
    // This binding is two-way, the keyboard might input in the backing textarea.
    // We'll have to update the contenteditable caret position and input value.
    const _lContentEditorBackingInput = $('<textarea>')
      .addClass("ml_log_message_box_backing_input")
      .val(_lContentEditor.text());

    _lContentEditorBackingInput.on("input", event => {
      const inputValue = event.target.value;
      if (inputValue !== _lContentEditor.text()) {
        _lContentEditor[0].innerHTML = inputValue;
        helpers.setContentEditableCaretPosition(_lContentEditor[0], _lContentEditorBackingInput[0].selectionStart);
        _lContentEditor.trigger("input");
      }
    });

    const _lPreventPropagation = function (pEvent) { pEvent.stopPropagation(); };

    // Disable certain behaviors that are hijacked by jquery UI
    _lContentEditor.on("keyup", _lPreventPropagation);
    _lContentEditor.on("mousedown", _lPreventPropagation);

    _lContentEditor.on('input', () => {
      const lCursorRestoreFunc = helpers.getContentEditableCursorPositionRestoreFunc(_lContentEditor[0], _lContentEditorBackingInput[0]);

      const _lProcessed = _lProcessValue(_lContentEditor.text());
      _lContentEditor.html(_lProcessed.displayedValue);

      _lContentEditorBackingInput.val(_lContentEditor.text());
      _lContentEditorBackingInput[0].dispatchEvent(new Event('input'));

      lCursorRestoreFunc();
    });

    _lContentEditor.on('keydown', function (pEvent) {
      pEvent.stopPropagation();
      if (pEvent.which === 13 && !pEvent.shiftKey) {
        pEvent.preventDefault();
        _lContentEditor.blur();
      }
    });

    _lContentEditor.on("focus", () => {
      _lContentEditorBackingInput[0].dispatchEvent(new Event("focus"));
    });

    _lContentEditor.on("mouseup", () => {
      // The cursor position is set after the focus event happens
      const caretPosition = helpers.getContentEditableCaretPosition(_lContentEditor[0]);
      _lContentEditorBackingInput[0].setSelectionRange(caretPosition, caretPosition);
    });

    _lContentEditor.on("blur", () => {
      const _lText = _lContentEditor.text().trimRight();
      const _lProcessed = _lProcessValue(_lText);
      pOnValue(_lProcessed.savedValue);

      _lContentEditorBackingInput[0].dispatchEvent(new Event("blur"));
    });

    return _lWidget.append(
      _lWidgetContainer.append(
        _lContentEditor,
        _lContentEditorBackingInput,
      ),
    );
  }

  /* Module export glue */
  /* global define */
  const gExports = {
    axisParamWidget: axisParamWidget,
    box: box,
    condition: condition,
    loop: loop,
    sequenceListWidget: sequenceListWidget,
    variableListWidget : variableListWidget,
    logMessageWidget: logMessageWidget,
    // deprecated
    expressionWidget: expressionWidget,
    floatParamWidget: floatParamWidget,
    integerParamWidget: integerParamWidget,
    variableNameWidget: variableNameWidget,
  };
  const gModule = typeof module;
  if (gModule !== 'undefined' && module.exports != undefined) {
    module.exports = gExports;
  } else if (define != undefined && typeof define === 'function' && define.amd) {
    define([], function () { return gExports; });
  }
}());
